import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, X } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    employeeId: '',
    aadharNumber: '',
    dateOfJoining: '',
    password: '',
    confirmPassword: '',
    role: 'Sales Executive'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ name: 'Vooxy', logo: null });
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const loginPath = tenantSlug ? `/t/${tenantSlug}/login` : '/';

  useEffect(() => {
    axios.get(`${API}/company-settings`).then(resp => {
      const name = resp.data?.company_name?.trim();
      const logo = resp.data?.logo_url;
      setBranding({
        name: name || 'Vooxy',
        logo: logo ? (logo.startsWith('http') ? logo : `${BACKEND_URL}${logo}`) : null,
      });
      if (name) document.title = `Join ${name} — CRM`;
    }).catch(() => {});
  }, []);

  const roles = [
    'Sales Executive',
    'Service Executive',
    'Sales Coordinator',
    'Tender Executive',                   // iter89
    'Regional Sales Manager',
    'National Sales Manager',
    'Vice President',
    'President'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/signup`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        employee_id: formData.employeeId,
        aadhar_number: formData.aadharNumber,
        date_of_joining: formData.dateOfJoining
      });
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F9F7]">
        <div className="medical-card p-8 max-w-md w-full text-center" data-testid="signup-success">
          <div className="w-16 h-16 bg-[#E2F3F0] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-[#00a69c]" weight="fill" />
          </div>
          <h2 className="text-2xl font-bold text-[#052A23] mb-2" style={{ fontFamily: 'Outfit' }}>
            Registration Successful!
          </h2>
          <p className="text-[#6D8E87] mb-6">
            Your account is pending approval from an administrator. You'll be able to login once approved.
          </p>
          <button
            onClick={() => navigate(loginPath)}
            className="btn-primary w-full"
            data-testid="back-to-login-button"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1677611998429-1baa4371456b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdyZWVuJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzU3MTczNzZ8MA&ixlib=rb-4.1.0&q=85')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#00a69c]/90 to-[#007a72]/90 flex items-center justify-center">
          <div className="text-white text-center p-8">
            {branding.logo && (
              <img src={branding.logo} alt={branding.name} className="w-24 h-24 object-contain mx-auto mb-4 rounded-2xl bg-white/15 p-2 backdrop-blur-sm" data-testid="signup-company-logo" />
            )}
            <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>Join Our Team</h1>
            <p className="text-xl" style={{ fontFamily: 'IBM Plex Sans' }} data-testid="signup-company-name">{branding.name} CRM Portal</p>
            <p className="text-sm mt-2 opacity-70">powered by Vooxy</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F4F9F7]">
        <div className="w-full max-w-md">
          <div className="medical-card p-8">
            <h2 className="text-3xl font-bold text-[#052A23] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="signup-title">
              Create Account
            </h2>
            <p className="text-[#6D8E87] mb-6">Sign up to get started</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center justify-between" data-testid="signup-error">
                <span>{error}</span>
                <X size={20} className="cursor-pointer" onClick={() => setError('')} />
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="signup-form">
              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="John Doe"
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="you@hospital.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Employee ID *</label>
                <input
                  type="text"
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="EMP001"
                  required
                  data-testid="employee-id-input"
                />
              </div>

              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Aadhar Card Number *</label>
                <input
                  type="text"
                  value={formData.aadharNumber}
                  onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="XXXX XXXX XXXX"
                  pattern="[0-9]{12}"
                  maxLength="12"
                  required
                  data-testid="aadhar-input"
                />
                <p className="text-xs text-[#6D8E87] mt-1">Enter 12-digit Aadhar number</p>
              </div>

              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Date of Joining *</label>
                <input
                  type="date"
                  value={formData.dateOfJoining}
                  onChange={(e) => setFormData({ ...formData, dateOfJoining: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  required
                  data-testid="date-of-joining-input"
                />
              </div>

              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  required
                  data-testid="role-select"
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="••••••••"
                  required
                  minLength="6"
                  data-testid="password-input"
                />
              </div>

              <div className="mb-6">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2">Confirm Password *</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="••••••••"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00a69c] text-white py-3 rounded-full font-medium hover:bg-[#00bfb3] transition-all duration-200 disabled:opacity-50 mb-4"
                data-testid="signup-button"
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>

              <p className="text-center text-sm text-[#6D8E87]">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate(loginPath)}
                  className="text-[#00a69c] font-medium hover:underline"
                  data-testid="login-link"
                >
                  Sign In
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
